import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Clock,
  Trophy,
  AlertTriangle,
  Zap,
  TrendingUp,
  Star
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface Insight {
  id: string
  type: 'info' | 'success' | 'warning' | 'alert'
  icon: React.ReactNode
  title: string
  value: string
  description?: string
}

interface QuickInsightsProps {
  peakHour?: { hour: number; count: number }
  topAgent?: { name: string; chats: number }
  avgWaitTime?: number
  queueWarning?: boolean
  bookingsToday?: number
  loading?: boolean
  /** The active dashboard timeframe — used to swap the "today" word in card
   *  copy so the labels match the user's selected range. */
  timeframe?: 'today' | 'yesterday' | '7d' | '30d' | '90d'
}

const RANGE_NOUN: Record<NonNullable<QuickInsightsProps['timeframe']>, string> = {
  today: 'today',
  yesterday: 'yesterday',
  '7d': 'in the last 7 days',
  '30d': 'in the last 30 days',
  '90d': 'in the last 90 days',
}

const RANGE_TITLE_SUFFIX: Record<NonNullable<QuickInsightsProps['timeframe']>, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': '— Last 7 Days',
  '30d': '— Last 30 Days',
  '90d': '— Last 90 Days',
}

export default function QuickInsights({
  peakHour,
  topAgent,
  avgWaitTime,
  queueWarning,
  bookingsToday,
  loading = false,
  timeframe = 'today',
}: QuickInsightsProps) {
  const rangeNoun = RANGE_NOUN[timeframe]
  const rangeTitleSuffix = RANGE_TITLE_SUFFIX[timeframe]
  const formatHour = (hour: number) => {
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:00 ${ampm}`
  }

  const insights: Insight[] = []

  // Peak hour insight
  if (peakHour) {
    insights.push({
      id: 'peak-hour',
      type: 'info',
      icon: <Clock className="h-4 w-4" />,
      title: `Peak Hour ${rangeTitleSuffix}`,
      value: formatHour(peakHour.hour),
      description: `${peakHour.count} chats during this hour`
    })
  }

  // Top agent insight
  if (topAgent) {
    insights.push({
      id: 'top-agent',
      type: 'success',
      icon: <Trophy className="h-4 w-4" />,
      title: `Top Performer ${rangeTitleSuffix}`.trim(),
      value: topAgent.name,
      description: `${topAgent.chats} chats handled ${rangeNoun}`
    })
  }

  // Queue warning
  if (queueWarning && avgWaitTime && avgWaitTime > 120) {
    insights.push({
      id: 'queue-warning',
      type: 'alert',
      icon: <AlertTriangle className="h-4 w-4" />,
      title: 'Queue Alert',
      value: `${Math.floor(avgWaitTime / 60)}+ min wait`,
      description: 'Consider adding more agents'
    })
  }

  // Bookings insight — value is range-aware (same metric used by the
  // dashboard headline), so the label needs to match.
  if (bookingsToday !== undefined && bookingsToday > 0) {
    insights.push({
      id: 'bookings',
      type: 'success',
      icon: <Star className="h-4 w-4" />,
      title: `Bookings ${rangeTitleSuffix}`,
      value: bookingsToday.toString(),
      description: 'Appointments scheduled'
    })
  }

  // Response time insight — only when there's enough data to be meaningful.
  // Showing "0s avg" was misleading: it triggered when the metric was missing
  // or sub-second, and it contradicted the longer "27s avg response" shown in
  // the agent leaderboard below. Require ≥1s and ≤60s to render the card.
  if (avgWaitTime !== undefined && avgWaitTime >= 1 && avgWaitTime <= 60) {
    insights.push({
      id: 'fast-response',
      type: 'success',
      icon: <Zap className="h-4 w-4" />,
      title: 'Fast Responses',
      value: `${Math.round(avgWaitTime)}s avg`,
      description: 'Great response time!'
    })
  }

  const getInsightStyles = (type: Insight['type']) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-emerald-500/10',
          border: 'border-emerald-500/20',
          icon: 'text-emerald-500',
          badge: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
        }
      case 'warning':
        return {
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/20',
          icon: 'text-amber-500',
          badge: 'bg-amber-500/10 text-amber-700 border-amber-500/20'
        }
      case 'alert':
        return {
          bg: 'bg-red-500/10',
          border: 'border-red-500/20',
          icon: 'text-red-500',
          badge: 'bg-red-500/10 text-red-700 border-red-500/20'
        }
      default:
        return {
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/20',
          icon: 'text-blue-500',
          badge: 'bg-blue-500/10 text-blue-700 border-blue-500/20'
        }
    }
  }

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Quick Insights</CardTitle>
          <CardDescription>AI-powered highlights</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse flex gap-3">
                <div className="h-10 w-10 rounded-lg bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-3 w-32 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (insights.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Quick Insights</CardTitle>
          <CardDescription>AI-powered highlights</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <TrendingUp className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              Insights will appear as activity increases
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Quick Insights</CardTitle>
            <CardDescription>AI-powered highlights</CardDescription>
          </div>
          <Badge variant="outline" className="text-xs">
            Live
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="space-y-3">
          {insights.slice(0, 4).map((insight) => {
            const styles = getInsightStyles(insight.type)
            return (
              <div
                key={insight.id}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-lg border transition-colors',
                  styles.bg,
                  styles.border
                )}
              >
                <div className={cn(
                  'flex items-center justify-center h-8 w-8 rounded-lg',
                  styles.bg,
                  styles.icon
                )}>
                  {insight.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {insight.title}
                    </p>
                    {insight.type === 'alert' && (
                      <Badge variant="outline" className={cn('text-xs', styles.badge)}>
                        Action
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm font-semibold truncate">{insight.value}</p>
                  {insight.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {insight.description}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
